import React, {Fragment, useEffect, useState, useMemo, useCallback} from 'react'
import {useDispatch, useSelector} from "react-redux"
import {useDropzone} from 'react-dropzone'
import DateFnsUtils from "@date-io/date-fns"
// import WebViewer from '@pdftron/webviewer'

// Material-UI Core
import {makeStyles, createMuiTheme} from '@material-ui/core/styles'
import Fab from '@material-ui/core/Fab'
import Paper from '@material-ui/core/Paper'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Tooltip from '@material-ui/core/Tooltip'
import MenuItem from '@material-ui/core/MenuItem'
import TextField from '@material-ui/core/TextField'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemText from '@material-ui/core/ListItemText'
import Avatar from '@material-ui/core/Avatar'
import ListItemAvatar from '@material-ui/core/ListItemAvatar'
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction'
import Divider from '@material-ui/core/Divider'
import Box from '@material-ui/core/Box'
import {MuiPickersUtilsProvider, KeyboardDatePicker} from "@material-ui/pickers"

// Material-UI Lab
import Autocomplete from '@material-ui/lab/Autocomplete'

// Material-UI Icon
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import DescriptionIcon from '@material-ui/icons/Description'
import LaunchIcon from '@material-ui/icons/Launch'
import BeenhereIcon from '@material-ui/icons/Beenhere'
import GetAppIcon from '@material-ui/icons/GetApp'

// Custom Component
import useLoading from "../Loading/useLoading"
import LockScreenLoading from '../LockScreenLoading/LockScreenLoading'
import {Alert} from '../Alert/Alert'
import ConfirmDialog from '../Dialog/ConfirmDialog'
import WebViewer from "../WebViewer/WebViewer"
import createFileChecksum from '../../global/createFileChecksum'


// Custom Style
import {propertyDetailFormStyle} from '../../styles/jss/components/fileManager/propertyDetailFormStyle'

// Fetch Data
import {getPropertyDetail, getCompanyList, removePropertyFile, setPrimary, fileUpload, addFileProperty, attachmentDownload} from '../../dataSource/fileManager'

// Global Function
// import {webViewerLoadDocument} from "../../global/webViewerLoadDocument"


const useStyles = makeStyles(theme => propertyDetailFormStyle(theme))

const PropertyDetailForm = props => {
	const classes = useStyles()
	const store = require('store')
	const dispatch = useDispatch()
	const {folderRight, propertyFileSeq, setPropertyFileSeq, primaryFilename, webViewerProps, setWebViewerProps, validate, validationErrors} = props

	// Redux Data
	const propertyDetail = useSelector(state => state.propertyDetail)
	const propertySeqRedux = useSelector(state => state.propertySeqRedux)
	const primaryPropertyFileSeqRedux = useSelector(state => state.primaryPropertyFileSeqRedux)
	const propertyFileRedux = useSelector(state => state.propertyFileRedux)
	const selectedFolderRedux = useSelector(state => state.selectedFolderRedux)

	// Loading
	const [fetchPropertyResultLoading, fetchPropertyResultShowLoading, fetchPropertyResultHideLoading] = useLoading()

	const [companyListResult, setCompanyListResult] = useState([])
	const [propertyDetailResult, setPropertyDetailResult] = useState(null)
	const [performRefresh, setPerformRefresh] = useState(null)
	const [removeFileConfirmDialogOpen, setRemoveFileConfirmDialogOpen] = useState(false)
	const [setPrimaryConfirmDialogOpen, setSetPrimaryConfirmDialogOpen] = useState(false)
	const [lockScreenLoadingOpen, setLockScreenLoadingOpen] = useState(false)
	const [progressTitle, setProgressTitle] = useState("")
	
	const [alertOpen, setAlertOpen] = useState(false)
	const [alertData, setAlertData] = useState({
		type: "", 
		title: "", 
		content: ""
	})


	const theme = createMuiTheme()
	theme.typography.h6 = {
		fontSize: '0.4rem',
		fontWeight: "bold",
		'@media (min-width:600px)': {
			fontSize: '0.7rem',
			fontWeight: "bold",
		},
		[theme.breakpoints.up('lg')]: {
			fontSize: '1rem',
			fontWeight: "bold",
		},
	}

	const baseStyle = {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		borderWidth: 2,
		borderRadius: 2,
		borderColor: '#FFFFFF',
		borderStyle: 'dashed',
		backgroundColor: '#FFFFFF',
		color: '#000000',
		outline: 'none',
		transition: 'border .24s ease-in-out',
		minHeight: theme.spacing(30)
	}

	const activeStyle = {
		borderColor: "blue"
	}

	const acceptStyle = {
		borderColor: '#00e676'
	}

	const rejectStyle = {
		borderColor: '#ff1744'
	}

	const onDrop = useCallback(acceptedFiles => {
		fileUploadAction(acceptedFiles)
	})

	const {getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, open} = useDropzone({
		accept: 'application/pdf, image/*, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document',
		noClick: true,
		onDrop
	}, [])

	const style = useMemo(() => ({
		...baseStyle,
		...(isDragActive ? activeStyle : {}),
		...(isDragAccept ? acceptStyle : {}),
		...(isDragReject ? rejectStyle : {})
	}), [isDragActive, isDragReject, isDragAccept])


	const fileUploadAction = acceptedFilesPool => {
		const fileSeq = []

		acceptedFilesPool.map(file => {
			// const checksum = await createFileChecksum(file)

			try {
				fileUpload(file).then(res => {
					console.log("Upload Attachment File Successful")
					console.log(res)
	
					fileSeq.splice(0, fileSeq.length)
					fileSeq.push(res.data[0].file_seq)
	
					addFileProperty(propertySeqRedux, fileSeq).then( res => {
						setPerformRefresh(res)
	
						buildAlert({
							// Alert Type: success, info, warning, error
							type: "success",
							title: "success Alert",
							content: "Add new attachment successful."
						})
					})
				})
			}
			catch (error) {
				buildAlert({
					// Alert Type: success, info, warning, error
					type: "error",
					title: "Error Alert",
					content: `${error}`
				})
			}

		})
	}


	const handleChange = event => {
		const {name, value} = event.target
		const res = {...propertyDetailResult}
		res[name] = value

		setPropertyDetailResult(res)
		dispatch({
			type: "UPDATE_PROPERTY_DETAIL",
			data: res
		})

		// if (validationErrors.company_name && validationErrors.company_name !== "" ? true : false) {
		// 	validate(res)
		// }
	}


	const handleCompanySelectChange = (event, value) => {
		if (value) {
			const res = {...propertyDetailResult}
			
			res.company_seq = value ? value.company_seq : 0
			res.company_name = value ? value.company_name : ""

			setPropertyDetailResult(res)
			dispatch({
				type: "UPDATE_PROPERTY_DETAIL",
				data: res
			})

			// if (validationErrors.company_name && validationErrors.company_name !== "" ? true : false) {
			//	validate(res)
			// }
		}
	}


	const handleDateChange = key => (date, value) => {
		const res = {...propertyDetailResult}
		res[key] = date

		setPropertyDetailResult(res)
		dispatch({
			type: "UPDATE_PROPERTY_DETAIL",
			data: res
		})
	}



	const setPrimaryAction = () => {
		setPrimary(propertySeqRedux, propertyFileRedux).then(res => {
			setPerformRefresh(res)

			buildAlert({
				// Alert Type: success, info, warning, error
				type: "success",
				title: "Success Alert",
				content: "The primary file have been changed"
			})
		})
			.catch(error => {
				buildAlert({
					// Alert Type: success, info, warning, error
					type: "error",
					title: "Error Alert",
					content: "The primary file change failure"
				})
			})
	}

	const removePropertyFileAction = () => {
		removePropertyFile(propertySeqRedux, propertyFileRedux).then(res => {
			setPerformRefresh(res)

			buildAlert({
				// Alert Type: success, info, warning, error
				type: "success",
				title: "Success Alert",
				content: "The file have been remove"
			})
		})
		.catch(error => {
			buildAlert({
				// Alert Type: success, info, warning, error
				type: "error",
				title: "Error Alert",
				content: "The file remove failure"
			})
		})
	}


	const downloadAttachmentAction = (propertySeq, propertyFileSeq) => () => {
		// console.log("Property Seq: ", propertySeq)
		// console.log("Property File Seq: ", propertyFileSeq)

		setProgressTitle("Downloading...")
		setLockScreenLoadingOpen(true)

		attachmentDownload(propertySeq, propertyFileSeq).then(() => {
			console.log("Attachment file download successful.")
		
			setLockScreenLoadingOpen(false)
			setProgressTitle("")
		})
	}


	const setPrimaryConfirmDialog = propertyFileSeq => event => {
		dispatch({
			type: "PROPERTY_FILE_SELECT",
			data: propertyFileSeq
		})

		setSetPrimaryConfirmDialogOpen(true)
	}

	const setPrimaryConfirmAction = () => {
		setSetPrimaryConfirmDialogOpen(false)
		setPrimaryAction()
	}

	const setPrimaryCancelAction = () => {
		setSetPrimaryConfirmDialogOpen(false)

		dispatch({
			type: "PROPERTY_FILE_UNSELECT",
			data: null
		})
	}


	const removePropertyFileConfirmDialog = propertyFileSeq => event => {
		dispatch({
			type: "PROPERTY_FILE_SELECT",
			data: propertyFileSeq
		})

		setRemoveFileConfirmDialogOpen(true)
	}

	const removeFileConfirmAction = () => {
		setRemoveFileConfirmDialogOpen(false)
		removePropertyFileAction()
	}

	const removeFileCancelAction = () => {
		setRemoveFileConfirmDialogOpen(false)

		dispatch({
			type: "PROPERTY_FILE_UNSELECT",
			data: ""
		})
	}

	const buildAlert = (data) => {
		setAlertData({
			type: data.type,
			title: data.title,
			content: data.content
		})

		setAlertOpen(true)
	}


	const getWebViewerInstance = propertyFileSeq => () => {
		setPropertyFileSeq(propertyFileSeq)
		// webViewerLoadDocument("efiling", "dms", propertySeqRedux, propertyFileSeq, primaryFilename)

		setWebViewerProps({
			from: "API",
			autoLoading: false,
			file_cat: "dms",
			file_ref: propertyFileSeq,
			file_name: primaryFilename
		})

	}


	// useEffect(() => {
	// 	if (document.getElementById('webviewer')) {
	// 		document.getElementById('webviewer').innerHTML = ''
	// 	}
	// 	console.log(document.getElementById('webviewer'))

	// 	WebViewer({
	// 		path: process.env.REACT_APP_PDFTRON_PATH,
	// 		licenseKey: process.env.REACT_APP_PDFTRON_LICENSE_KEY,
	// 		// config: 'config.js' // path/to/your/config.js
	// 		// config: '../../config/pdfTron.js'
	// 		// config: 'config/pdfTron.js'

	// 	}, document.getElementById('webviewer')).then(instance => {
	// 		webViewerLoadDocument("efiling", "dms", propertySeqRedux, primaryPropertyFileSeqRedux, primaryFilename)
	// 	})
	// }, [])

	useEffect(() => {
		fetchPropertyResultShowLoading()

		getPropertyDetail(propertySeqRedux).then(async res => {
			setPropertyDetailResult(res.data[0])
			dispatch({
				type: "UPDATE_PROPERTY_DETAIL",
				data: res.data[0]
			})

			setPropertyFileSeq(primaryPropertyFileSeqRedux)
			
			getCompanyList(selectedFolderRedux).then(res => {
				setCompanyListResult(res)
			})

			fetchPropertyResultHideLoading()
		})
	}, [performRefresh])


	return (
		// console.log("Folder Right | Variable: folderRight (PropertyDetailForm.js)"),
		// console.log(folderRight),

		// console.log("webViewer Property | Variable: webViewerProps (PropertyDetailForm.js)"),
		// console.log(webViewerProps),

		// console.log("Selected Folder | Variable: selectedFolderRedux (PropertyDetailForm.js)"),
		// console.log(selectedFolderRedux),

		// console.log("Property Files Detail Information | Variable: propertyDetailResult (PropertyDetailForm.js)"),
		// console.log(propertyDetailResult),

		// console.log("Property Files Detail Information (Redux) | Variable: propertyDetail (PropertyDetailForm.js)"),
		// console.log(propertyDetail),

		// console.log("Property Seqance (Redux) | Variable: propertySeqRedux (PropertyDetailForm.js)"),
		// console.log(propertySeqRedux),

		// console.log("Seqance for Property Files Selected | Variable: propertyFileSeq (PropertyDetailForm.js)"),
		// console.log(propertyFileSeq),

		// console.log("Property Seq for Selected  | Variable: propertySeqRedux (PropertyDetailForm.js)"),
		// console.log(propertySeqRedux),

		// console.log("Company List By Folder | Variable: companyListResult (PropertyDetailForm.js)"),
		// console.log(companyListResult),

		// console.log("Primary Property Files Seq for Selected | Variable: primaryPropertyFileSeqRedux (PropertyDetailForm.js)"),
		// console.log(primaryPropertyFileSeqRedux),

		// console.log("Validation Error | Variable: validationErrors (PropertyDetailForm.js)"),
		// console.log(validationErrors),

		// console.log("---------------------------------------------------------------------------------"),

		<Fragment>
			<div>
				<LockScreenLoading 
					open={lockScreenLoadingOpen}
					circleSize={200}
					progressTitle={progressTitle}
				/>

				<ConfirmDialog
					open={removeFileConfirmDialogOpen}
					dialogTitle={"Remove Folder"}
					dialogContent={"Are you sure to remove this file."}
					confirmAction={removeFileConfirmAction}
					cancelAction={removeFileCancelAction}
				/>

				<ConfirmDialog
					open={setPrimaryConfirmDialogOpen}
					dialogTitle={"Change Primary File"}
					dialogContent={"Are you sure to set this file to primary."}
					confirmAction={setPrimaryConfirmAction}
					cancelAction={setPrimaryCancelAction}
				/>

				<Alert
					open={alertOpen}
					alertType={alertData.type}
					alertTitle={alertData.title}
					alertContent={alertData.content}
					handleClose={() => setAlertOpen(false)}
				/>
			</div>

			
			<Grid container spacing={1}>
				<Grid item xs={12} sm={12} md={5}>
					{fetchPropertyResultLoading}
					
					{
						propertyDetailResult !== null ?
							<div className={classes.detailInformation}>
								<form className={classes.root} noValidate autoComplete="off">
									<TextField
										id="category"
										name="category"
										label="Category"
										required
										fullWidth={true}
										size="small"
										variant="outlined"
										value={propertyDetailResult != null ? propertyDetailResult.folder_path[1] : ""}
										InputProps={{
											readOnly: true,
										}}
									/>

									<TextField
										id="type"
										name="type"
										label="Type"
										required
										fullWidth={true}
										size="small"
										variant="outlined"
										value={propertyDetailResult != null ? propertyDetailResult.folder_path[2] : ""}
										InputProps={{
											readOnly: true,
										}}
									/>

									<TextField
										id="document_code"
										name="document_code"
										label="File Code"
										type="text"
										required
										fullWidth={true}
										size="small"
										variant="outlined"
										value={propertyDetailResult != null ? propertyDetailResult.document_code : ""}
										InputProps={{
											readOnly: true,
										}}
									/>


									<MuiPickersUtilsProvider utils={DateFnsUtils} >
										<KeyboardDatePicker
											id="date_of_document"
											name="date_of_document"
											label="Date of Document"
											format="dd/MM/yyyy"
											inputVariant="outlined"
											placeholder="DD/MM/YYYY"
											value={propertyDetailResult ? propertyDetailResult.date_of_document : null}
											size="small"
											KeyboardButtonProps={{
												"aria-label": "change date",
												disabled: !folderRight.write,
											}}

											InputProps={{
												readOnly: !folderRight.write,
											}}

											onChange={handleDateChange("date_of_document")}
											className={classes.datePickerField}
										/>
									</MuiPickersUtilsProvider>

									<Autocomplete
										id="company_name"
										name="company_name"
										options={companyListResult}
										getOptionLabel={option => option.company_name}
										value={propertyDetailResult ? {company_seq: propertyDetailResult.company_seq, company_name: propertyDetailResult.company_name} : ""}
										onChange={(event, value) => {
											handleCompanySelectChange(event, value)
										}}
										renderInput={params => (
											<TextField
												{...params}
												required
												variant="outlined"
												label="Organization"
												size="small"
												InputLabelProps={{
													shrink: true,
												}}
												InputProps={{
													...params.InputProps,
													endAdornment : null
												}}
												error={validationErrors.company_seq && validationErrors.company_seq !== "" ? true : false}
												helperText={validationErrors.company_seq ? validationErrors.company_seq : ""}
											/>
										)}
									/>

									<TextField
										select
										id="inout"
										name="inout"
										label="IN/OUT"
										required
										fullWidth={true}
										size="small"
										variant="outlined"
										onChange={handleChange}
										value={propertyDetailResult != null ? propertyDetailResult.inout : ""}
										error={validationErrors.inout && validationErrors.inout !== "" ? true : false}
										helperText={validationErrors.inout}
										InputProps={{
											readOnly: !folderRight.write,
										}}
									>
										<MenuItem key={"NA"} value={"NA"}><em>NA</em></MenuItem>
										<MenuItem key={"IN"} value={"IN"}>IN</MenuItem>
										<MenuItem key={"OUT"} value={"OUT"}>OUT</MenuItem>
									</TextField>

									<TextField
										select
										id="is_confidential"
										name="is_confidential"
										label="Confidential"
										required
										fullWidth={true}
										size="small"
										variant="outlined"
										onChange={handleChange}
										value={propertyDetailResult != null ? propertyDetailResult.is_confidential ? true : false : ""}
										InputProps={{
											readOnly: !folderRight.write,
										}}
									>
										<MenuItem key={false} value={false}>NO</MenuItem>
										<MenuItem key={true} value={true}>YES</MenuItem>
									</TextField>

									<TextField
										id="ref_no"
										name="ref_no"
										label="Reference Code"
										// required
										fullWidth={true}
										size="small"
										variant="outlined"
										onChange={handleChange}
										value={propertyDetailResult != null ? propertyDetailResult.ref_no : ""}
										error={validationErrors.ref_no && validationErrors.ref_no !== "" ? true : false}
										helperText={validationErrors.ref_no}
										InputProps={{
											readOnly: !folderRight.write,
										}}
									/>

									<TextField
										id="file_subject"
										name="file_subject"
										label="Subject"
										// required
										multiline
										rows={4}
										fullWidth={true}
										size="small"
										variant="outlined"
										onChange={handleChange}
										value={propertyDetailResult != null ? propertyDetailResult.file_subject : ""}
										error={validationErrors.file_subject && validationErrors.file_subject !== "" ? true : false}
										helperText={validationErrors.file_subject}
										InputProps={{
											readOnly: !folderRight.write,
										}}
									/>

									<TextField
										id="remark"
										name="remark"
										label="Remark"
										multiline
										rows={4}
										fullWidth={true}
										size="small"
										variant="outlined"
										onChange={handleChange}
										value={propertyDetailResult != null ? propertyDetailResult.remark : ""}
										InputProps={{
											readOnly: !folderRight.write,
										}}
									/>
								</form>

								<Box border={1} borderRadius="borderRadius" borderColor="grey.400" className={classes.fileList}>
									<div {...getRootProps(folderRight.write ? {style} : {className: 'dropzone disabled'})}>
										<input {...getInputProps()} />

										<Typography variant="button">Attachment</Typography>
										<List className={classes.attachmentToolbar}>
											{
												propertyDetailResult != null ? Array.isArray(propertyDetailResult.property_files) ?
													propertyDetailResult.property_files.map(atth => {
														return (
															<div key={atth.property_file_seq}>
																<ListItem key={atth.filename}>
																	<ListItemAvatar>
																		<Avatar>
																			<DescriptionIcon />
																		</Avatar>
																	</ListItemAvatar>

																	<ListItemText
																		primary={atth.filename}
																		secondary={atth.is_primary ? 'This file is primary file' : null}
																	/>

																	<ListItemSecondaryAction xs={4}>
																		<Tooltip title="Download File">
																			<Fab color="primary" size="small" aria-label="setPrimary" className={classes.attachmentBtn} onClick={downloadAttachmentAction(propertyDetailResult.property_seq, atth.property_file_seq)}>
																				<GetAppIcon />
																			</Fab>
																		</Tooltip>

																		{
																			folderRight ?
																				folderRight.write ?
																					<Tooltip title="Set Primary">
																						<Fab color="primary" size="small" aria-label="setPrimary" className={classes.attachmentBtn} onClick={setPrimaryConfirmDialog(atth.property_file_seq)}>
																							<BeenhereIcon />
																						</Fab>
																					</Tooltip>
																				: ""
																			: ""
																		}

																		<Tooltip title="View File">
																			<Fab color="primary" size="small" aria-label="view" className={classes.attachmentBtn} onClick={getWebViewerInstance(atth.property_file_seq)}>
																				<LaunchIcon />
																			</Fab>
																		</Tooltip>

																		{
																			folderRight ? 
																				folderRight.write ?
																					<Tooltip title="Delete File">
																						<Fab color="primary" size="small" aria-label="delete" className={classes.attachmentBtn} onClick={removePropertyFileConfirmDialog(atth.property_file_seq)}>
																							<DeleteIcon />
																						</Fab>
																					</Tooltip>
																				: ""
																			: ""
																		}
																		
																	</ListItemSecondaryAction>
																</ListItem>
																<Divider variant="inset" component="li" />
															</div>
														)
													}) : "" : ""
											}
										</List>

										{
											folderRight ? 
												folderRight.write ?
													<Box className={classes.fileUploadBtnArea}>
														<Tooltip title="Choose File">
															<Fab color="primary" size="small" aria-label="upload_file" className={classes.fileUploadBtn} onClick={open}>
																<AddIcon />
															</Fab>
														</Tooltip>
													</Box>
												: ""
											: ""
										}
									</div>
								</Box>
							</div>
						: ""
					}
				</Grid>

				<Grid item xs={12} sm={12} md={7}>
					<div>
						{/* <div className={classes.webviewer} id="webviewer"></div> */}
						<WebViewer
							property={webViewerProps}
							readOnly={!folderRight.write}
							zoom={false}
							move={false}
							ocr={false}
							closeDoc={false}
						/>
					</div>
				</Grid>
			</Grid>
		</Fragment>
	)
}


export default PropertyDetailForm